<script lang="ts">
	import { resized } from '#components/image.svelte'
	import { paths } from '#components/shape.svelte'
	import { Tween } from 'svelte/motion'
	import { cubicInOut } from 'svelte/easing'
	import { untrack } from 'svelte'
	import { genID } from 'kitto'

	interface Props {
		/** A shape from shape.svelte; changing it morphs from the last one. */
		name: string
		src: string
		alt?: string | null
		focus?: string | null
	}

	let { name, src, alt, focus }: Props = $props()

	/**
	 * Every shape is drawn into one 6:5 box, as N evenly spaced points along its line, so any shape
	 * can ease into any other point by point. The prospectus's shapes are all 6:5; one that isn't
	 * still works, just squashed to fit. The points are what's drawn at rest too: swapping back to
	 * the real path at the end would snap the corners the points cut. At 400 they cut about a pixel.
	 */
	const W = 600
	const H = 500
	const N = 400
	const duration = 700

	const id = genID()
	let probe: SVGPathElement | undefined = $state()

	const shape = (name: string) => paths[(name in paths ? name : '6_5-06') as keyof typeof paths]

	/** The shape's line as N points in the box, then its stroke width: one flat list to tween. */
	function sample(name: string) {
		const { path, width, height, stroke } = shape(name)
		probe!.setAttribute('d', path)
		const length = probe!.getTotalLength()
		const [sx, sy] = [W / width, H / height]
		const points: Array<number> = []
		for (let i = 0; i < N; i++) {
			const { x, y } = probe!.getPointAtLength((length * i) / (N - 1))
			points.push(x * sx, y * sy)
		}
		return [...points, (stroke * (sx + sy)) / 2]
	}

	/** If the next line runs the other way round, flip it, so the morph doesn't turn inside out. */
	function align(from: Array<number>, to: Array<number>) {
		const end = 2 * N - 2
		const gap = (a: number, b: number) => Math.hypot(from[a] - to[b], from[a + 1] - to[b + 1])
		if (gap(0, end) + gap(end, 0) >= gap(0, 0) + gap(end, end)) return to
		const flipped: Array<number> = []
		for (let i = N - 1; i >= 0; i--) flipped.push(to[2 * i], to[2 * i + 1])
		return [...flipped, to[2 * N]]
	}

	const line = new Tween<Array<number>>([], { duration, easing: cubicInOut })

	$effect(() => {
		const next = name
		if (!probe) return
		untrack(() => {
			const to = sample(next)
			// the first shape (and every one, with reduced motion) is just drawn; after that, each new
			// one eases from wherever the line is, even mid-morph
			if (!line.current.length || matchMedia('(prefers-reduced-motion: reduce)').matches) {
				line.set(to, { duration: 0 })
			} else {
				line.set(align(line.current, to))
			}
		})
	})

	const d = $derived.by(() => {
		const points = line.current
		let path = `M${points[0]} ${points[1]}`
		for (let i = 2; i < 2 * N; i += 2) path += `L${points[i]} ${points[i + 1]}`
		return path
	})

	// each new picture comes in on top of the old, inside the same moving shape; the old one goes
	// once the new one has settled
	let layers = $state([untrack(() => ({ key: 0, src, alt, focus }))])

	$effect(() => {
		const layer = { key: Date.now(), src, alt, focus }
		untrack(() => {
			if (layer.src !== layers.at(-1)?.src) layers.push(layer)
		})
	})

	/** Start a picture's entrance once it has loaded (or failed), so it never scales in empty. */
	function when_loaded(node: SVGImageElement, href: string) {
		const image = new window.Image()
		const show = () => node.classList.add('loaded')
		image.onload = image.onerror = show
		image.src = href
		if (image.complete) show()
	}
</script>

<figure class="morph">
	<svg class="svg" viewBox="0 0 {W} {H}" fill="none">
		<defs><path bind:this={probe} /></defs>
		<mask id="mask_{id}" maskUnits="userSpaceOnUse">
			{#if line.current.length}
				<path
					{d}
					stroke="white"
					stroke-width={line.current[2 * N]}
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			{:else}
				<!-- before the page runs: the shape's own path -->
				{@const { path, width, height, stroke } = shape(name)}
				<path
					d={path}
					transform="scale({W / width} {H / height})"
					stroke="white"
					stroke-width={stroke}
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			{/if}
		</mask>
		<!-- the mask is on the group, so a picture can scale without the shape scaling with it -->
		<g mask="url(#mask_{id})">
			{#each layers as layer (layer.key)}
				{@const href = resized(layer.src, { width: W, height: H, quality: 85, focus: layer.focus })}
				<image
					class="layer"
					{href}
					width={W}
					height={H}
					preserveAspectRatio="xMidYMid slice"
					role={layer.alt ? 'img' : undefined}
					aria-label={layer.alt || undefined}
					use:when_loaded={href}
					onanimationend={() => (layers = layers.filter(({ key }) => key >= layer.key))}
				/>
			{/each}
		</g>
	</svg>
</figure>

<style lang="css">
	.morph {
		aspect-ratio: 6 / 5;
		width: 100%;
	}

	.svg {
		display: block;
		size: 100%;
	}

	/* hidden until loaded, then it fades in from a little larger and settles */
	.layer {
		opacity: 0;
		transform-box: fill-box;
		transform-origin: center;

		&:global(.loaded) {
			animation: settle 1.1s cubic-bezier(0.22, 1, 0.36, 1) both;
		}
	}

	@keyframes settle {
		from {
			opacity: 0;
			scale: 1.15;
		}
		to {
			opacity: 1;
			scale: 1;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.layer:global(.loaded) {
			animation-duration: 1ms;
		}
	}
</style>
